import { ReactSortable } from 'react-sortablejs';
import {useSafeState} from "ahooks";
import { dropdownCard } from '@/pages/datalab/components/SortableDrag/Sortable/dropdownCard';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { ISortableOption } from '@/pages/datalab/components/SortableDrag/typeDefine';
import {checkNullString} from '@/pages/datalab/utils/utils';
import React, { useMemo, useState } from 'react';
import {
  TDispatchFun,
  TItemOnChange,
  SortableItems,
} from '@/pages/datalab/components/SortableDrag/configration';
import { dateType } from '@/pages/datalab/components/SortableDrag/typeDefine';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { SortableAffix } from '@/pages/datalab/components/SortableDrag/Sortable/sortableAffix';
import { initDragStatus } from '@/pages/datalab/components/SortableDrag/utils';
import { createFilterSortablePersist } from '@/pages/datalab/components/SortableDrag/utils/filterSortablePersist';
import {useIntl, useDispatch} from "umi";
import {duplicateNameVerification, RenameWrapper} from "@/pages/datalab/components/SortableDrag/FieldEditComponents";
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import {ITableJoinRelation} from "@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface";

export interface FilterSortableProps {
  sortableKey: string;
  sortableList: IColumnInfo[];
  sortableOption: ISortableOption;
  setSortableOption: Function;
  dispatchFun: TDispatchFun;
  dataTables: IGetTableInfoDto[];
  dataTableJoinRelations: ITableJoinRelation[];
  chartType: string;
  mode: string;
  dimensions: any[];
  showCustomColumnModel: Function;
  system: string;
  customColumns?: CustomColumn[];
  /** 传入时覆盖 ChartType 自动拼接的占位后缀；传空字符串则仅展示「批量添加或拖拽」 */
  dragPlaceholderSuffix?: string;
}

/** 筛选条件 Sortable 区：拖拽、批量添加、筛选器编辑与删除 */
export const FilterSortable = (props: FilterSortableProps) => {
  const { locale } = useIntl();
  const isEn = locale === 'en-US';
  const {
    sortableKey,
    sortableList,
    sortableOption,
    setSortableOption,
    dispatchFun,
    dataTables,
    chartType,
    dimensions,
    showCustomColumnModel,
    dataTableJoinRelations,
    system,
    mode,
    customColumns,
    dragPlaceholderSuffix,
  } = props;
  const { dragState, dragMoveHandle } = sortableOption;
  /** 本次拖拽源字段，供 onMove 校验与 onRemove 对比时间联动 */
  const [currentDragItem, setCurrentDragItem] = useState<IColumnInfo>();
  /** 重命名编辑中时禁用 Sortable，避免拖拽与输入冲突 */
  const [renameSelectComp, setRenameSelectComp] = useSafeState<string>('');
  const dispatch = useDispatch();

  /** 筛选区写回逻辑单例：随 chartType/mode/dimensions 变化重建，保证副作用上下文正确 */
  const filterPersist = useMemo(
    () =>
      createFilterSortablePersist({
        sortableKey,
        chartType,
        mode,
        dimensions,
        dispatchFun,
        dispatch,
      }),
    [sortableKey, chartType, mode, dimensions, dispatchFun, dispatch],
  );

  const {
    errorDrag: { [sortableKey]: errorDragType },
  } = dragState;

  /** 筛选器下拉/弹层内字段值变更，委托 filterPersist 统一写回 */
  const itemOnChange: TItemOnChange = (columnInfo, isUpdate) => {
    filterPersist.changeItem(sortableList, columnInfo, isUpdate);
  };

  /** 重命名展示名，走 updateTableField 持久化到表元数据（非 model 内 columnFilters） */
  const updateHandle = (column: IColumnInfo, updateColumn: Record<string, unknown>) => {
    dispatchFun(
      'updateTableField',
      {
        table_id: column.table_id,
        isEn,
        lan: isEn ? 'en' : 'zh',
        column,
        updateColumn,
      },
    );
  };

  /** 占位区后缀文案：业务页可覆盖；否则按 ChartType 提示所需筛选数量 */
  let dragSuffixText = '';
  if (dragPlaceholderSuffix !== undefined) {
    dragSuffixText = dragPlaceholderSuffix;
  } else if (sortableList.length === 2 && chartType) {
    const { filter } = ChartTypeInfos[chartType] || {};
    dragSuffixText = (isEn ? ` ${filter?.info?.en}` : filter?.info?.cn) || '';
  }

  /** 已存在对比时间配置的日期筛选器（全局只允许一个） */
  const hasCompareTimesFilter = sortableList.find((item: any) => item?.compareTimes?.length > 0);
  /** 部分图表类型或表格原始列模式不支持对比时间 */
  const hideCompare =
  [
    ChartType.Pie,
    ChartType.HorizontalBarChart,
    ChartType.StackBar,
    ChartType.PercentStackBar,
    ChartType.PercentLine,
    ChartType.MixedChart,
    ChartType.Trend,
    ChartType.DimNumberTrend,
    ChartType.Map,
    ChartType.WordCloud,
  ].includes(chartType) ||
    (chartType === ChartType.Table && mode === ChartCalculateMode.RawColumn);

  const existingFilterColumns = sortableList.filter(
    item => !checkNullString(item.id),
  );
  /** 批量添加入口配置：写回走 filterPersist，与 close 删除副作用对齐 */
  const batchAddConfig = {
    enabled: true,
    targetKey: sortableKey as 'columnFilters',
    dataTables,
    customColumns: customColumns || [],
    existingColumns: existingFilterColumns,
    dispatchFun,
    chartType,
    mode,
    isEn,
    filterPersist,
  };

  return (
    <ReactSortable
      group="groupName"
      handle={'.data-label-tips'}
      filter={'.js-drag-disable,.data-label-disabled,.data-label-tips-undrag'}
      preventOnFilter={false}
      animation={100}
      ghostClass="data-label-tips-blue"
      delay={50}
      forceFallback={true}
      list={sortableList}
      className={`form-data-label-tips filter-content ${sortableKey}-${SortableItems} ${dragState.currentDrag === sortableKey ? 'filter-acitve' : ''}`}
      disabled={!!renameSelectComp}
      setList={(list) => {
        // 拖拽非法（errorDrag）时拒绝写入，避免误落库
        if (dragState.currentDrag === sortableKey && !checkNullString(errorDragType)) {
          return false;
        }
        if (JSON.stringify(sortableList) !== JSON.stringify(list)) {
          filterPersist.saveList(list);
        }
      }}
      onMove={(evt: any, originalEvent: any) => {
        // 区内排序或跨区拖入时校验是否允许放置
        if (currentDragItem) {
          return dragMoveHandle(evt, originalEvent, dispatchFun, currentDragItem);
        }
        return false;
      }}
      onEnd={() => {
        // 与 metricSortable 对齐：拖拽结束重置 drag 状态，不再 saveDrag(errorDrag)
        initDragStatus(dispatchFun);
      }}
      onStart={(evt) => {
        setCurrentDragItem(sortableList[evt.oldIndex || 0]);
      }}
    >
      {sortableList.map((item, index) => {
        const type = dateType.values.includes(item.column_type || '') ? 'date' : '';
        // 非当前对比时间字段或未开启对比能力时，禁用「对比时间」入口
        const isDisabledCompare = hideCompare || (hasCompareTimesFilter ? (hasCompareTimesFilter?.id !== item?.id) : false);
        const granularity = item.column_granularity;
        return item.id ? (<RenameWrapper
          setRenameSelectComp={setRenameSelectComp}
          key={item.id + item.table_code}
          className={'blue'}
          isEn={isEn}
          oldName={item.column_display}
          changeRename={(name: string) => {
            const columns = dataTables?.find(({table_id}) => table_id === item.table_id)?.column_list || [];
            if (duplicateNameVerification(columns, name)) {
              return isEn ? 'Duplicate naming' : '重复命名';
            }
            updateHandle(item, {display_name: name});
          }}
          renderItem={(ref: any, clickRename: Function) => (
            <div
              className={`data-label-tips blue${
                // 非月粒度日期在特定态下不可拖拽（与筛选器弹层交互约束一致）
                type === 'date' && granularity !== 'month' && !sortableOption.canDrag ? ' js-drag-disable' : ''
              }`}
              style={{ display: 'flex' }}
              ref={ref}
            >
              {dropdownCard({ system, sortableKey, item, sortableOption, setSortableOption, dispatchFun, itemOnChange, locale, isDisabledCompare, clickRename: () => {clickRename();setRenameSelectComp(isEn ? item.column_display_en : item.column_display)}, updateHandle, showCustomColumnModel, dataTableJoinRelations, dataTables})}
              <div
                className="close-icon"
                onClick={() => {
                  filterPersist.removeItems(sortableList, [item]);
                }}
              >
                <i className="selectOther-flex-icon">
                  <SvgIcon type="clonse" />
                </i>
              </div>
            </div>
          )}/>) : (
          // 无 id 的占位项：展示批量添加 / 拖拽提示
          <SortableAffix key={index} item={item} dragState={dragState} sortableKey={sortableKey} dragSuffixText={dragSuffixText} batchAddConfig={batchAddConfig}/>
        );
      })}
    </ReactSortable>
  );
};
